import { Box, BoxProps, MenuItem, Tab, TabProps, Tabs, TabsProps, TextField } from '@mui/material';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import style from './index.module.scss';

interface TabPanelProps extends BoxProps {
  isActive: boolean;
  optimization?: boolean;
  keepInMemory?: boolean;
}

const TabPanel: React.FC<TabPanelProps> = ({
  isActive,
  optimization = true,
  children,
  className,
  keepInMemory = true,
  ...rest
}) => {
  const [isWasActive, setIsWasActive] = useState(!optimization);

  useEffect(() => {
    if (isActive) {
      setIsWasActive(true);
    }
  }, [isActive]);

  if (!isWasActive) return null;
  if (!keepInMemory && !isActive) return null;

  return (
    <Box
      className={clsx(style.panel, className, { [style.panelHidden]: !isActive })}
      hidden={!isActive}
      {...rest}
    >
      {children}
    </Box>
  );
};

export interface TabItem<T> {
  value: T;
  title: React.ReactNode;
  TabProps?: Partial<TabProps>;
  element: React.ReactNode;
  optimization?: boolean;
  keepInMemory?: boolean;
  error?: boolean;
  wrapperProps?: Partial<BoxProps>;
}

interface Classes {
  root: string;
  header: string;
  content: string;
  tab: string;
  tabs: string;
}

interface Slots {
  tabsTop: React.ReactNode;
  tabsBottom: React.ReactNode;
}

interface Props<TabValue> {
  keepInMemory?: boolean;
  className?: string;
  classes?: Partial<Classes>;
  tabs: TabItem<TabValue>[];
  value: TabValue;
  onChange: (value: this['value']) => void;
  children?: React.ReactNode;
  TabsProps?: Partial<TabsProps>;
  TabPanelProps?: Partial<BoxProps>;
  RootProps?: Partial<BoxProps>;
  slots?: Partial<Slots>;
  /**
   * N2 (T3): below md the strip is a compact full-width select -- the same
   * tabs as its options, the same value and `onChange` (so the same query
   * param). For a nested (level-2) strip; the desktop keeps its tabs.
   */
  selectBelowMd?: boolean;
}

export const TabsWrapper = <TabValue extends string>({
  keepInMemory,
  className,
  TabsProps,
  TabPanelProps,
  RootProps,
  classes,
  tabs,
  value,
  onChange,
  slots,
  children,
  selectBelowMd,
}: Props<TabValue>) => {
  // N1 (brief pattern C): below md a strip keeps its selected tab in view. MUI
  // scrolls it flush with the scroller's edge before the scroll buttons arrive
  // (an IntersectionObserver), and in RTL does not scroll again when they take
  // their 40 px: a tab chosen near the end rested half hidden. Here the tab is
  // centred -- room on both sides for a scroll button that arrives -- and
  // centred again once the buttons have settled (scrolling is what enables
  // the start one). Desktop keeps MUI's own scrolling.
  const isBelowMd = useIsBelowMd();
  const tabsRef = useRef<HTMLDivElement>(null);
  const isSelect = Boolean(selectBelowMd) && isBelowMd;
  useEffect(() => {
    if (!isBelowMd || isSelect) {
      return;
    }
    const centre = () =>
      tabsRef.current
        ?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
        ?.scrollIntoView({ block: 'nearest', inline: 'center' });
    const timers = [300, 800].map((delay) => window.setTimeout(centre, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [isBelowMd, isSelect, value]);

  return (
    <Box
      className={clsx(style.root, classes?.root, className, RootProps?.className)}
      {...RootProps}
    >
      <div className={clsx(style.header, classes?.header)}>
        {slots?.tabsTop}
        {children}
        {isSelect ? (
          <TextField
            select
            fullWidth
            size={'small'}
            variant={'standard'}
            className={style.select}
            value={value}
            onChange={(e) => onChange(e.target.value as TabValue)}
          >
            {tabs.map((tab) => (
              <MenuItem key={tab.value} value={tab.value} className={style.selectItem}>
                {tab.title}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Tabs
            variant={'scrollable'}
            scrollButtons={'auto'}
            // PD-08 / M-07..M-09 (R2). `scrollButtons: 'auto'` is not enough on a
            // phone: without this flag MUI adds `scrollButtonsHideMobile`, which
            // hides the buttons below sm, so the strips scrolled 353 / 436 /
            // 356 px with no cue at all and simply cut the last label mid-word.
            // Above sm the buttons already showed when a strip overflowed, so
            // this changes nothing on the desktop baselines.
            allowScrollButtonsMobile
            indicatorColor={'secondary'}
            {...TabsProps}
            value={value}
            onChange={(e, v) => onChange(v)}
            className={clsx(classes?.tabs)}
            ref={tabsRef}
          >
            {tabs.map((tab) => {
              return (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.title}
                  {...tab?.TabProps}
                  className={clsx(style.tab, tab?.TabProps?.className, classes?.tab, {
                    [style.tabError]: tab.error,
                  })}
                />
              );
            })}
          </Tabs>
        )}
        {slots?.tabsBottom}
      </div>
      <div className={clsx(style.content, classes?.content)}>
        {tabs.map((tab) => {
          const __keepInMemory = tab.keepInMemory === undefined ? keepInMemory : tab.keepInMemory;
          return (
            <TabPanel
              key={tab.value}
              optimization={tab.optimization}
              isActive={value === tab.value}
              keepInMemory={__keepInMemory}
              {...TabPanelProps}
            >
              {tab.element}
            </TabPanel>
          );
        })}
      </div>
    </Box>
  );
};
